import { useEffect, useState } from "react"
import { useLogisticsPermissions } from "../hooks/use-logistics-permissions"
import { Alert, Badge, Button, Card, Group, Loader, Modal, Select, Stack, Table, Text } from "@mantine/core"
import { IconAlertTriangle, IconArrowRight, IconCheck, IconClockPause, IconInbox } from "@tabler/icons-react"
import { openConfirmModal } from "@mantine/modals"
import dayjs from "dayjs"
import { headerStore } from "@shared/state/header.state.ts"
import { useDeliveryStops, useAssignPendingGroup, useDecidePendingGroup, usePendingProductGroups } from "../hooks/use-delivery-stops"
import { PENDING_GROUP_STATUS, PendingProductGroup, PRODUCTO_TIPO } from "../domain/delivery-stop.types"
import { esCorrelativoEcuador } from "../utils/logistics-country"

const TIPO_BADGE_COLOR: Record<string, string> = { suelto: "indigo", bandeja: "violet", activo: "orange", paquete: "grape" }
const TIPO_LABEL: Record<string, string> = { suelto: "Productos sueltos", bandeja: "Bandejas", activo: "Activos", paquete: "Paquetes" }
const STATUS_LABEL: Record<string, string> = { [PENDING_GROUP_STATUS.PENDING]: "Pendiente de decisión", [PENDING_GROUP_STATUS.DEFERRED]: "En espera" }
const STATUS_COLOR: Record<string, string> = { [PENDING_GROUP_STATUS.PENDING]: "yellow", [PENDING_GROUP_STATUS.DEFERRED]: "gray" }

function ProductosTable({ group }: { group: PendingProductGroup }) {
  const productos = group.productos ?? []
  if (group.tipo === PRODUCTO_TIPO.SUELTO) {
    return (
      <Table fz={12} striped withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Producto</Table.Th>
            <Table.Th>Picking</Table.Th>
            <Table.Th style={{ textAlign: "right" }}>Cant.</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {productos.map((p, i) => {
            const qty = p.lots?.reduce((acc, lot) => acc + (lot.qty ?? 0), 0)
            return (
              <Table.Tr key={i}>
                <Table.Td>{p.productCode ? `${p.productCode} — ${p.productName ?? ""}` : (p.productName ?? "—")}</Table.Td>
                <Table.Td>{p.pickingName ?? "—"}</Table.Td>
                <Table.Td style={{ textAlign: "right" }}>{qty ?? "—"}</Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    )
  }
  if (group.tipo === PRODUCTO_TIPO.BANDEJA) {
    return (
      <Table fz={12} striped withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Bandeja</Table.Th>
            <Table.Th style={{ textAlign: "right" }}>Activos dentro</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {productos.map((b, i) => (
            <Table.Tr key={i}>
              <Table.Td>{b.codigoUnico ? `${b.codigoUnico} — ${b.trayName ?? ""}` : (b.trayName ?? "Bandeja")}</Table.Td>
              <Table.Td style={{ textAlign: "right" }}>{b.activos?.length ?? 0}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    )
  }
  if (group.tipo === PRODUCTO_TIPO.ACTIVO) {
    return (
      <Table fz={12} striped withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Activo</Table.Th>
            <Table.Th>Producto vinculado</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {productos.map((a, i) => (
            <Table.Tr key={i}>
              <Table.Td>{a.activoCode ? `${a.activoCode} — ${a.activoName ?? ""}` : (a.activoName ?? "—")}</Table.Td>
              <Table.Td>{a.productName ?? "—"}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    )
  }
  return (
    <Table fz={12} striped withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Paquete</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {productos.map((p, i) => (
          <Table.Tr key={i}>
            <Table.Td>{p.paqueteName ?? "—"}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

function AssignModal({ group, onClose }: { group: PendingProductGroup; onClose: () => void }) {
  const assignMutation = useAssignPendingGroup()
  const [stopId, setStopId] = useState<string | null>(null)
  const { data: candidates, isFetching } = useDeliveryStops({ search: group.soName, rows: 50 })

  const options = (candidates?.data ?? [])
    .filter((s) => s.id !== group.originStopId)
    .map((s) => ({ value: s.id, label: `${s.correlativo ?? s.id} — ${s.address ?? "sin dirección"}` }))

  const handleAssign = async () => {
    if (!stopId) return
    await assignMutation.mutateAsync({ id: group.id, stopId })
    onClose()
  }

  return (
    <Modal opened onClose={onClose} title={`Asignar a otro punto de entrega — SO ${group.soName ?? group.soId}`} centered>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Solo se muestran puntos que ya tienen esta SO como ítem — el punto destino debe existir primero (agregar la SO ahí antes de asignar).
        </Text>
        <Select
          label="Punto de entrega destino"
          placeholder={isFetching ? "Buscando puntos con esta SO..." : "Seleccione un punto"}
          data={options}
          value={stopId}
          onChange={setStopId}
          searchable
          disabled={isFetching}
          nothingFoundMessage="No se encontraron otros puntos con esta SO"
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancelar
          </Button>
          <Button leftSection={<IconArrowRight size={16} />} disabled={!stopId} loading={assignMutation.isPending} onClick={handleAssign}>
            Asignar
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function PendingGroupCard({ group }: { group: PendingProductGroup }) {
  const decideMutation = useDecidePendingGroup()
  const permisos = useLogisticsPermissions()
  const [assigning, setAssigning] = useState(false)

  const handleInclude = () => {
    openConfirmModal({
      title: "Incluir productos en el punto de origen",
      centered: true,
      children: (
        <Text size="sm">
          ¿Incluir estos {group.productos?.length ?? 0} producto(s) en el punto{" "}
          <strong>{group.originStopCorrelativo ?? group.originStopId}</strong>? Se re-sincronizará con {esCorrelativoEcuador(group.originStopCorrelativo) ? "Odoo" : "Odoo/DISET"} y con la app móvil.
        </Text>
      ),
      labels: { confirm: "Incluir", cancel: "Cancelar" },
      confirmProps: { color: "green", leftSection: <IconCheck size={15} /> },
      onConfirm: () => decideMutation.mutate({ id: group.id, decision: "include" })
    })
  }

  const handleDefer = () => decideMutation.mutate({ id: group.id, decision: "defer" })

  return (
    <Card withBorder radius="md" padding="md">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Group gap={6} wrap="wrap">
            <Badge color={TIPO_BADGE_COLOR[group.tipo ?? ""] ?? "gray"}>{TIPO_LABEL[group.tipo ?? ""] ?? group.tipo}</Badge>
            <Badge variant="light" color={STATUS_COLOR[group.status ?? ""] ?? "gray"}>
              {STATUS_LABEL[group.status ?? ""] ?? group.status}
            </Badge>
            {group.isEmergency && (
              <Badge color="red" variant="filled" leftSection={<IconAlertTriangle size={11} />}>
                Urgente
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed">
            {group.createdAt ? dayjs(group.createdAt).format("DD/MM/YYYY HH:mm") : ""}
          </Text>
        </Group>

        <Group gap="xl" wrap="wrap">
          <Text size="sm">
            <Text span fw={600}>
              SO:
            </Text>{" "}
            {group.soName ?? group.soId}
          </Text>
          <Text size="sm">
            <Text span fw={600}>
              Paciente:
            </Text>{" "}
            {group.patientName ?? "—"}
          </Text>
          <Text size="sm">
            <Text span fw={600}>
              Médico:
            </Text>{" "}
            {group.doctorName ?? "—"}
          </Text>
          <Text size="sm">
            <Text span fw={600}>
              Punto de origen:
            </Text>{" "}
            {group.originStopCorrelativo ?? group.originStopId}
          </Text>
        </Group>

        {group.motivo && (
          <Text size="sm">
            <Text span fw={600}>
              Motivo:
            </Text>{" "}
            {group.motivo}
          </Text>
        )}

        <ProductosTable group={group} />

        {permisos.stopAssign && (
        <Group justify="flex-end" gap="xs">
          {group.status !== PENDING_GROUP_STATUS.DEFERRED && (
            <Button variant="light" color="gray" leftSection={<IconClockPause size={16} />} loading={decideMutation.isPending} onClick={handleDefer}>
              Dejar en espera
            </Button>
          )}
          <Button variant="light" leftSection={<IconArrowRight size={16} />} onClick={() => setAssigning(true)}>
            Asignar a otro punto
          </Button>
          <Button color="green" leftSection={<IconCheck size={16} />} loading={decideMutation.isPending} onClick={handleInclude}>
            Incluir en punto de origen
          </Button>
        </Group>
        )}
      </Stack>
      {assigning && <AssignModal group={group} onClose={() => setAssigning(false)} />}
    </Card>
  )
}

export default function PendingProductsPage() {
  const { data: groups, isFetching, refetch } = usePendingProductGroups()

  useEffect(() => {
    headerStore.setTitle("Productos Pendientes de Asignar")
    headerStore.setOnRefresh(() => refetch())
    return () => {
      headerStore.setTitle(null)
      headerStore.setOnRefresh(null)
    }
  }, [refetch])

  return (
    <Stack gap="md">
      <Alert color="blue" variant="light">
        Productos que llegaron de Odoo para una SO cuyo punto de entrega ya estaba confirmado o completado. No se agregan solos — hay que decidir si
        incluirlos en el punto de origen, asignarlos a otro punto, o dejarlos en espera.
      </Alert>

      {isFetching && !groups?.length ? (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      ) : !groups?.length ? (
        <Card withBorder radius="md" padding="xl">
          <Stack align="center" gap="xs">
            <IconInbox size={32} opacity={0.5} />
            <Text c="dimmed">No hay productos pendientes de asignar.</Text>
          </Stack>
        </Card>
      ) : (
        <Stack gap="md">
          {groups.map((group) => (
            <PendingGroupCard key={group.id} group={group} />
          ))}
        </Stack>
      )}
    </Stack>
  )
}
